"use client";

import { useState, useEffect, useCallback } from "react";
import { useMembership } from "@/lib/hooks/useMembership";

import { expandToOccurrences, formatDateParam } from "@/lib/utils/recurrence";
import { useMySignups } from "@/lib/hooks/useMySignups";
import { storyblokEditable } from "@storyblok/react/rsc";

import { EventCard } from "@/components/layout/EventCard";
import { EventTag } from "@/components/layout/EventTag";
import { GridContainer } from "@/components/layout/GridContainer";
import { Button } from "@/components/ui/button";
import { EventPreviewModal, type EventPreviewData } from "@/components/landing/EventPreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import type { EventStoryblok } from "@/component-types-sb";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";

// Inline type definition until Storyblok types are regenerated
type EventListSectionStoryblok = {
  title?: string;
  count?: number;
  show_filters?: boolean;
  category?: string;
  _uid: string;
  component: "event_list_section";
  [k: string]: any;
};

type EventListSectionProps = {
  blok: EventListSectionStoryblok;
};

type EventData = {
  uuid: string;
  title: string;
  date: Date;
  originalDate: Date;
  endDate?: Date;
  eventType: string;
  ctaLink: string;
  memberOnly: boolean;
  description?: any;
  image?: { src: string; alt: string };
  experts?: { name: string; image?: { filename: string; alt?: string } }[];
};

// Event categories for filtering
const eventCategories = [
  { value: "all", label: "Alle Events" },
  { value: "diaTalk", label: "DiaTalk" },
  { value: "topicTalk", label: "TopicTalks" },
  { value: "sprechstunde", label: "Sprechstunde" },
  { value: "community", label: "Community" },
  { value: "reisen", label: "Reisen" },
] as const;



const EventListSection = ({ blok }: EventListSectionProps) => {
  const isLanding = useIsLandingPage();
  const { isMember } = useMembership();
  const { isSignedUp } = useMySignups();
  const [events, setEvents] = useState<EventData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewEvent, setPreviewEvent] = useState<EventPreviewData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>(blok.category || "all");
  const [visibleCount, setVisibleCount] = useState(blok.count || 6);

  const initialCount = blok.count || 6;

  /**
   * `category` gibt es nur an `event` — Reisen tragen den Typ selbst.
   * Deshalb landet der Kategoriefilter in `eventOnlyFilter` und wirkt nur
   * auf die event-Haelfte der Abfrage.
   */
  const buildEventOnlyFilter = useCallback((category: string) => {
    if (category === "all") return undefined;
    if (category === "reisen") {
      // "Reisen" means two things to a visitor: the trips themselves
      // (content type `travel`, no category) and the free info sessions
      // about them (still `event`, category `reiseInfo`). The travel query
      // covers the former, this filter the latter.
      return { category: { in: "reiseInfo" } };
    }
    return { category: { in: category } };
  }, []);

  const mapStoriesToEvents = useCallback((stories: any[]): EventData[] => {
    const results: EventData[] = [];
    for (const story of stories) {
      const occurrences = expandToOccurrences(story);
      for (const occ of occurrences) {
        const content = story.content as EventStoryblok;
        results.push({
          uuid: story.uuid,
          title: content.title,
          date: occ.displayDate,
          originalDate: occ.originalDate,
          endDate: occ.endDate,
          eventType: eventTypeFromContent(content),
          ctaLink: occ.isRepeating
            ? `/${story.full_slug}?date=${formatDateParam(occ.originalDate)}`
            : `/${story.full_slug}`,
          memberOnly: content.member_access === "member_only",
          description: content.text,
          image: content.image?.filename
            ? { src: content.image.filename, alt: content.image.alt || content.title }
            : undefined,
          experts: isLanding
            ? ((content as any).experts || [])
                .filter((e: any) => typeof e !== "string" && e?.content)
                .map((e: any) => ({
                  name: e.content.name,
                  image: e.content.image,
                }))
            : undefined,
        });
      }
    }
    return results.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [isLanding]);

  // Fetch when category changes
  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      setEvents([]);

      try {
        const shared = {
          sort_by: "content.date:asc" as const,
          per_page: 100,
          ...(isLanding && { resolve_relations: "event.experts" }),
        };

        // Eine echte Kategorie (diaTalk, community, ...) gibt es nur an
        // Events — Reisen mitzuladen wuerde die Liste verfaelschen. Nur bei
        // "all" und "reisen" gehoeren beide Typen hinein.
        const wantsTravel =
          selectedCategory === "all" || selectedCategory === "reisen";

        const stories = wantsTravel
          ? await fetchEventsAndTravels({
              ...shared,
              eventOnlyFilter: buildEventOnlyFilter(selectedCategory),
            })
          : await fetchStoryblokList({
              ...shared,
              content_type: "event",
              filter_query: buildEventOnlyFilter(selectedCategory),
            });

        if (stories && stories.length > 0) {
          setEvents(mapStoriesToEvents(stories));
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
    setVisibleCount(initialCount);
  }, [
    selectedCategory,
    buildEventOnlyFilter,
    mapStoriesToEvents,
    initialCount,
    isLanding,
  ]);

  const visibleEvents = events.slice(0, visibleCount);
  const hasMore = visibleCount < events.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + initialCount, events.length));
  };

  return (
    <GridContainer
      {...storyblokEditable(blok)}
      id="event-section"
      className="py-4 !px-0"
    >
      <section className="col-span-12 md:col-span-10 md:col-start-2 bg-sand-50 rounded-3xl px-4 py-6 flex flex-col gap-4 w-full items-center">
        {/* Title */}
        <h2 className="text-headline-xl text-black text-center">
          {blok.title || "Aktuelle Events"}
        </h2>

        {/* Category Filter Tags */}
        {blok.show_filters !== false && (
          <div className="flex flex-wrap gap-1 justify-center w-full">
            {eventCategories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className="cursor-pointer"
              >
                {category.value === "all" ? (
                  <span
                    className={`text-subline-sm inline-flex items-center gap-1 rounded-full px-2 py-1 text-black-80 ${
                      selectedCategory === "all"
                        ? "bg-white"
                        : "bg-white opacity-50"
                    }`}
                  >
                    {category.label}
                  </span>
                ) : (
                  /* Filter-Chip, kein Event-Badge: Hier steht der Typ zur
                     Auswahl, nicht der Zugang eines konkreten Events. Deshalb
                     bewusst ohne `memberOnly` — die Farbe kaeme sonst aus
                     einem Zustand, den es an dieser Stelle nicht gibt. */
                  <EventTag
                    variant={category.value}
                    inactive={selectedCategory !== category.value}
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col gap-4 w-full">
            {[...Array(Math.min(initialCount, 3))].map((_, i) => (
              <div
                key={i}
                className="w-full h-[88px] bg-sand-100 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && events.length === 0 && (
          <p className="text-copy text-center text-gray-100">
            Keine Events gefunden
          </p>
        )}

        {/* Event List */}
        {!isLoading && visibleEvents.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full items-stretch">
            {visibleEvents.map((event, index) => (
              <EventCard
                key={`${event.ctaLink}-${index}`}
                variant="densed"
                eventType={event.eventType}
                date={event.date}
                endDate={event.endDate}
                title={event.title}
                ctaLink={event.ctaLink}
                memberOnly={event.memberOnly}
                isMember={isMember}
                className="w-full max-w-none h-full"
                signedUp={isSignedUp(event.uuid, event.originalDate.toISOString())}
                onPreviewClick={isLanding ? () => {
                  setPreviewEvent({
                    title: event.title,
                    date: event.date,
                    endDate: event.endDate,
                    eventType: event.eventType,
                    image: event.image,
                    memberOnly: event.memberOnly,
                    description: event.description,
                    experts: event.experts,
                  });
                  trackEvent("content_preview_opened", {
                    type: "event",
                    title: event.title,
                    source: "event_list",
                    eventType: event.eventType,
                  });
                } : undefined}
              />
            ))}
          </div>
        )}

        {/* Load More Button */}
        {!isLoading && hasMore && (
          <Button
            variant="black"
            onClick={handleLoadMore}
            >
            Mehr laden
          </Button>
        )}
      </section>
      {previewEvent && (
        <EventPreviewModal event={previewEvent} onClose={() => setPreviewEvent(null)} />
      )}
    </GridContainer>
  );
};

export default EventListSection;
